import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { FaChevronDown, FaTimes, FaSearch } from "react-icons/fa";
import { AnimatePresence, motion } from "framer-motion";
import ActivityCard from "../Components/cards/ActivityCard";
import { toCardModel } from "../Components/cards/cardModel";
import "./activityFilter.css";

const FilterSelect = ({ label, options, value, onChange, placeholder = `All ${label}` }) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const hasValue = value.length > 0;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (triggerRef.current && !triggerRef.current.contains(event.target) &&
          dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleOptionClick = (option) => {
    onChange(option);
  };

  const handleChipRemove = (option, e) => {
    e.stopPropagation();
    onChange(option);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setIsOpen(false);
      triggerRef.current?.focus();
    }
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setIsOpen(!isOpen);
    }
    if (e.key === "ArrowDown" && !isOpen) {
      e.preventDefault();
      setIsOpen(true);
    }
  };

  return (
    <div className="filter-select" ref={triggerRef}>
      <button
        type="button"
        className="filter-select__trigger"
        ref={triggerRef}
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={`${label} filter${hasValue ? `, ${value.length} selected` : ''}`}
        data-has-value={hasValue}
      >
        <span className={hasValue ? "filter-select__selected" : "filter-select__placeholder"}>
          {hasValue ? (
            value.map(v => (
              <span key={v} className="filter-select__chip">
                {v}
                <button
                  type="button"
                  className="filter-select__chip-remove"
                  onClick={(e) => handleChipRemove(v, e)}
                  aria-label={`Remove ${v}`}
                >
                  <FaTimes aria-hidden="true" />
                </button>
              </span>
            ))
          ) : (
            placeholder
          )}
        </span>
        <FaChevronDown className="filter-select__icon" aria-hidden="true" />
      </button>

      <AnimatePresence>
        {isOpen && (
          <div className="filter-select__dropdown" ref={dropdownRef} role="listbox" aria-label={`${label} options`}>
            {options.map((option) => (
              <button
                key={option}
                type="button"
                className="filter-select__option"
                role="option"
                aria-selected={value.includes(option)}
                aria-checked={value.includes(option)}
                onClick={() => handleOptionClick(option)}
              >
                <span className="filter-select__checkbox" aria-hidden="true" />
                <span>{option}</span>
              </button>
            ))}
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

const DEFAULT_LEVEL_ORDER = ["A1", "A2", "B1", "B2", "C1"];

const ActivityFilter = ({
  data = [],
  categoryField = "category",
  levelField = "level",
  timeField = "time",
  searchFields = ["title", "description"],
  extraFilters = {},
  getLink,
  renderCard,
  ctaLabel = "",
  levelOrder = DEFAULT_LEVEL_ORDER,
  resultsLabel = "activities",
  itemsPerPage = 6,
  showLoadMore = true,
  infiniteScroll = true,
  scrollRootMargin = "400px",
  placeholderTexts = {},
  onFiltersChange,
}) => {
  const [visibleCount, setVisibleCount] = useState(itemsPerPage);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedLevels, setSelectedLevels] = useState([]);
  const [selectedTimes, setSelectedTimes] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [extraFilterValues, setExtraFilterValues] = useState({});

  const sentinelRef = useRef(null);
  const hasMoreRef = useRef(false);

  const loadMore = useCallback(() => {
    setVisibleCount((prev) => prev + itemsPerPage);
  }, [itemsPerPage]);

  const handleToggle = (value, state, setState) => {
    setState((prev) =>
      prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]
    );
  };

  const handleExtraFilterToggle = (filterKey, value) => {
    setExtraFilterValues((prev) => {
      const current = prev[filterKey] || [];
      const updated = current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value];
      return { ...prev, [filterKey]: updated };
    });
  };

  const clearAllFilters = () => {
    setSelectedCategories([]);
    setSelectedLevels([]);
    setSelectedTimes([]);
    setSearchQuery("");
    setExtraFilterValues({});
  };

  const hasActiveFilters = 
    selectedCategories.length > 0 || 
    selectedLevels.length > 0 || 
    selectedTimes.length > 0 || 
    searchQuery ||
    Object.values(extraFilterValues).some(arr => arr.length > 0);

  // Reset pagination when filters change
  useEffect(() => {
    setVisibleCount(itemsPerPage);
  }, [selectedCategories, selectedLevels, selectedTimes, searchQuery, extraFilterValues, itemsPerPage]);

  // Notify parent of filter changes
  useEffect(() => {
    if (onFiltersChange) {
      onFiltersChange({
        categories: selectedCategories,
        levels: selectedLevels,
        times: selectedTimes,
        search: searchQuery,
        extra: extraFilterValues,
      });
    }
  }, [selectedCategories, selectedLevels, selectedTimes, searchQuery, extraFilterValues, onFiltersChange]);

  // Filter logic
  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const matchesCategory = selectedCategories.length === 0 || selectedCategories.includes(item[categoryField]);
      const matchesLevel = selectedLevels.length === 0 || selectedLevels.includes(item[levelField]);
      const matchesTime = selectedTimes.length === 0 || selectedTimes.includes(item[timeField]);
      
      const query = searchQuery.toLowerCase().trim();
      const matchesSearch = query === "" || searchFields.some(field => 
        item[field]?.toLowerCase().includes(query)
      );

      // Extra filters
      let matchesExtra = true;
      for (const [filterKey, config] of Object.entries(extraFilters)) {
        const selected = extraFilterValues[filterKey] || [];
        if (selected.length > 0) {
          const fieldPath = config.fieldPath || filterKey;
          const itemValue = fieldPath.split('.').reduce((obj, key) => obj?.[key], item);
          if (!selected.includes(itemValue)) {
            matchesExtra = false;
            break;
          }
        }
      }

      return matchesCategory && matchesLevel && matchesTime && matchesSearch && matchesExtra;
    });
  }, [data, selectedCategories, selectedLevels, selectedTimes, searchQuery, extraFilterValues, extraFilters, categoryField, levelField, timeField, searchFields]);

  // Sort by level order
  const sortedData = useMemo(() => {
    return [...filteredData].sort(
      (a, b) => levelOrder.indexOf(a[levelField]) - levelOrder.indexOf(b[levelField])
    );
  }, [filteredData, levelField, levelOrder]);

  const currentData = sortedData.slice(0, visibleCount);

  const hasMore = visibleCount < sortedData.length;
  hasMoreRef.current = hasMore;

  // Automatically load more activities as the user scrolls down
  useEffect(() => {
    if (!infiniteScroll || !showLoadMore || !hasMore) return;

    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreRef.current) {
          loadMore();
        }
      },
      { root: null, rootMargin: scrollRootMargin }
    );

    observer.observe(node);

    // Fill the viewport if the remaining content does not create a scrollbar
    const fillViewport = () => {
      if (hasMoreRef.current && node.getBoundingClientRect().top < window.innerHeight) {
        loadMore();
        requestAnimationFrame(fillViewport);
      }
    };
    fillViewport();

    return () => observer.disconnect();
  }, [infiniteScroll, showLoadMore, hasMore, loadMore, scrollRootMargin]);

  // Extract unique values for filters
  const categoryOptions = useMemo(() => 
    [...new Set(data.map(t => t[categoryField]))].filter(Boolean).sort(), 
    [data, categoryField]
  );
  const levelOptions = useMemo(() => 
    [...new Set(data.map(t => t[levelField]))].filter(Boolean).sort((a, b) => levelOrder.indexOf(a) - levelOrder.indexOf(b)), 
    [data, levelField, levelOrder]
  );
  const timeOptions = useMemo(() => 
    [...new Set(data.map(t => t[timeField]))].filter(Boolean).sort(), 
    [data, timeField]
  );

  // Extra filter options
  const extraFilterOptions = useMemo(() => {
    const options = {};
    for (const [key, config] of Object.entries(extraFilters)) {
      const fieldPath = config.fieldPath || key;
      const values = [...new Set(data.map(item => 
        fieldPath.split('.').reduce((obj, k) => obj?.[k], item)
      ))].filter(Boolean).sort();
      options[key] = values;
    }
    return options;
  }, [data, extraFilters]);

  // Default card renderer. One shared card for every listing, so a grammar
  // index and a lesson plan look identical apart from their content.
  const defaultRenderCard = (item) => (
    <motion.li
      key={item.id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3 }}
    >
      <ActivityCard
        {...toCardModel(item, {
          to: getLink
            ? getLink(item)
            : `/${item[categoryField]}/${encodeURIComponent(String(item.id).toLowerCase())}`,
          categoryField,
          levelField,
          timeField,
          cta: ctaLabel,
        })}
      />
    </motion.li>
  );

  const CardRenderer = renderCard || defaultRenderCard;

  const categoryPlaceholder = placeholderTexts.category || "All Categories";
  const levelPlaceholder = placeholderTexts.level || "All Levels";
  const timePlaceholder = placeholderTexts.time || "All Times";

  return (
    <div className="home-container">
      <div className="topics-container">
        {/* Filter Bar */}
        <div className="filter-bar" role="search" aria-label="Filter exercises">
          <div className="filter-bar__search">
            <label htmlFor="activity-search" className="visually-hidden">Search activities</label>
            <div className="filter-bar__input-wrapper">
              <FaSearch className="filter-bar__search-icon" aria-hidden="true" />
              <input
                id="activity-search"
                type="search"
                className="filter-bar__input"
                placeholder="Search activities..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-describedby="search-hint"
              />
              {searchQuery && (
                <button
                  type="button"
                  className="filter-bar__clear-search"
                  onClick={() => setSearchQuery("")}
                  aria-label="Clear search"
                >
                  <FaTimes aria-hidden="true" />
                </button>
              )}
            </div>
            <span id="search-hint" className="visually-hidden">
              Type to filter activities by title or description
            </span>
          </div>

          <div className="filter-bar__selects">
            <FilterSelect
              label="Category"
              options={categoryOptions}
              value={selectedCategories}
              onChange={(v) => handleToggle(v, selectedCategories, setSelectedCategories)}
              placeholder={categoryPlaceholder}
            />
            <FilterSelect
              label="Level"
              options={levelOptions}
              value={selectedLevels}
              onChange={(v) => handleToggle(v, selectedLevels, setSelectedLevels)}
              placeholder={levelPlaceholder}
            />
            <FilterSelect
              label="Time"
              options={timeOptions}
              value={selectedTimes}
              onChange={(v) => handleToggle(v, selectedTimes, setSelectedTimes)}
              placeholder={timePlaceholder}
            />
            {Object.entries(extraFilters).map(([key, config]) => (
              <FilterSelect
                key={key}
                label={config.label || key}
                options={extraFilterOptions[key] || []}
                value={extraFilterValues[key] || []}
                onChange={(v) => handleExtraFilterToggle(key, v)}
                placeholder={placeholderTexts[key] || `All ${config.label || key}`}
              />
            ))}
          </div>

          {hasActiveFilters && (
            <button
              type="button"
              className="filter-bar__clear-all"
              onClick={clearAllFilters}
            >
              <FaTimes aria-hidden="true" />
              Clear All
            </button>
          )}
        </div>

        {/* Results Count */}
        <p className="results-count" aria-live="polite">
          Showing <strong>{currentData.length}</strong> of <strong>{filteredData.length}</strong> {resultsLabel}
        </p>

        {/* Activities Grid */}
        <ul className="topics-list" role="list">
          <AnimatePresence>
            {currentData.map((item) => CardRenderer(item))}
          </AnimatePresence>
        </ul>

        {/* Infinite scroll trigger / Load More fallback */}
        {showLoadMore && hasMore && (
          <div className="load-more-container">
            {infiniteScroll ? (
              <>
                <div
                  ref={sentinelRef}
                  className="load-more-sentinel"
                  aria-hidden="true"
                />
                <p className="load-more-status" role="status" aria-live="polite">
                  Loading more {resultsLabel}...
                </p>
              </>
            ) : (
              <button
                className="load-more-button"
                onClick={loadMore}
                aria-label={`Load more ${resultsLabel}. ${sortedData.length - visibleCount} remaining.`}
              >
                Load More
                <FaChevronDown className="load-more-button__icon" aria-hidden="true" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityFilter;